import "./DateTimePickerCalendar.css";

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    changeDateMonth,
    getCalendarRows,
} from "./../datepicker/Calendar";

import { ArrowDownSVG } from "../../../assets/icons";
import { DateTime } from "luxon";
import TimePicker from "./TimerPicker";

export interface IDateTimePickerCalendarProps {
    selectedDate: DateTime | undefined;
    onChange: (newDate: DateTime | undefined) => void;
    calendarPosition: {};
    onClickOutside: () => void;
    hasMinSelectableDate?: boolean;
    minDate?: DateTime | undefined;
    maxDate?: DateTime | undefined;
}

export function DateTimePickerCalendar(props: IDateTimePickerCalendarProps) {

    const ref = useRef<HTMLInputElement>(null);
    //const [shownDate, setShownDate] = useState<DateTime>(DateTime.now().startOf('day'));

    // Initialize shownDate respecting min and max dates
    const getInitialShownDate = () => {
        const now = DateTime.now().startOf('day');

        if (props.selectedDate) {
            // Ensure selectedDate is within bounds
            if (props.minDate && props.selectedDate < props.minDate) {
                return props.minDate;
            }
            if (props.maxDate && props.selectedDate > props.maxDate) {
                return props.maxDate;
            }
            return props.selectedDate;
        }

        // If no selected date, fall back to now within bounds
        if (props.minDate && now < props.minDate) {
            return props.minDate;
        }
        if (props.maxDate && now > props.maxDate) {
            return props.maxDate;
        }

        return now;
    };

    const [shownDate, setShownDate] = useState<DateTime>(getInitialShownDate());

    useEffect(() => {
        // Update shownDate when selectedDate changes, respecting bounds
        if (props.selectedDate) {
            setShownDate(getInitialShownDate());
        }
    }, [props.selectedDate, props.minDate, props.maxDate]);

    const currentDate = useMemo(() => {
        return DateTime.now();
    }, [DateTime.now().day]);

    useEffect(() => {
        if (props.selectedDate) setShownDate(props.selectedDate);
    }, [props.selectedDate]);



    /****************************
     * USER ACTIONS
     *****************************/

    const handleSelectDate = (value: DateTime | undefined) => {
        return () => props.onChange(value);
    };

    const rows = useMemo(() => getCalendarRows(shownDate), [shownDate]);

    const handleIconClick = useCallback(
        (isNextMonth: boolean) => {
            return () => {
                setShownDate(changeDateMonth(shownDate, isNextMonth));
            };
        },
        [setShownDate, shownDate]
    );

    const handleClickOutside = useCallback(
        (e: any) => {
            if (ref.current) {
                if (!ref.current.contains(e.target)) {
                    props.onClickOutside();
                    e.stopPropagation();
                }
            }
        },
        [props.onClickOutside]
    );

    useEffect(() => {
        document.addEventListener("click", handleClickOutside, true);
    }, [handleClickOutside]);


    const isLeftChevronIconDisabled = useMemo(() => {

        if (props.minDate) {
            const minYear = props.minDate.year;
            const minMonth = props.minDate.month;

            const shownYear = shownDate.year;
            const shownMonth = shownDate.month;

            const canGoBack = (shownMonth > minMonth && shownYear === minYear) || shownYear > minYear;
            return !canGoBack;
        }
        return false;

    }, [shownDate, props.minDate]);


    const isRightChevronIconDisabled = useMemo(() => {

        if (props.maxDate) {
            const maxYear = props.maxDate.year;
            const maxMonth = props.maxDate.month;

            const shownYear = shownDate.year;
            const shownMonth = shownDate.month;

            const canGoFoward = (shownMonth < maxMonth && shownYear === maxYear) || shownYear < maxYear;
            return !canGoFoward;
        }
        return false;

    }, [shownDate, props.maxDate]);


    /****************************
     * CSS & HTML
     *****************************/

    const renderDatePickerHeader = useMemo(() => {
        return (
            <div className={"datetime-picker-selector"}>
                <div
                    className={`datetime-picker-selector-icon datetime-picker-selector-icon-left ` + (isLeftChevronIconDisabled ? "disable" : "")}
                    onClick={handleIconClick(false)}
                >
                    <ArrowDownSVG />
                </div>
                <div className={"datetime-picker-selector-date"}>
                    {shownDate.toFormat("MMMM yyyy")}
                </div>
                <div
                    className={`datetime-picker-selector-icon datetime-picker-selector-icon-right ` + (isRightChevronIconDisabled ? "disable" : "")}
                    onClick={handleIconClick(true)}
                >
                    <ArrowDownSVG />
                </div>
            </div>);
    }, [handleIconClick, isLeftChevronIconDisabled, isRightChevronIconDisabled, shownDate]);

    const renderDatePickerCells = useMemo(() => {
        return (
            rows.map((cells, rowIndex) => (
                <div key={rowIndex} className={"datetime-picker-calendar-row"}>
                    {cells.map(({ text, value }, i) => (
                        <div
                            key={`${text} - ${i}`}
                            className={
                                `datetime-picker-calendar-cell datetime-picker-calendar-day-cell ` +
                                (value.month.valueOf() !== shownDate.month.valueOf() ||
                                    (props.minDate && props.minDate.startOf('day') > value.startOf('day')) ||
                                    (props.maxDate && props.maxDate.startOf('day') < value.startOf('day'))
                                    ? "out-of-selected-month "
                                    : "") +
                                (value.toFormat("dd/MM/yyyy") ===
                                    currentDate.toFormat("dd/MM/yyyy")
                                    ? "datetime-picker-calendar-day-cell-today "
                                    : "") +
                                (value.toFormat("dd/MM/yyyy") ===
                                    // (props.selectedDate &&
                                    //     props.selectedDate.toFormat("dd/MM/yyyy"))
                                    (shownDate &&
                                        shownDate.toFormat("dd/MM/yyyy"))
                                    ? "datetime-picker-calendar-day-cell-selected"
                                    : "")
                            }
                            onClick={
                                value.month.valueOf() === shownDate.month.valueOf()
                                    ?
                                    () => {
                                        // Ensure date respects min/max time limits
                                        let adjustedValue = value;

                                        if (props.minDate && value.hasSame(props.minDate, 'day')) {
                                            adjustedValue = value.set({
                                                hour: Math.max(value.hour, props.minDate.hour),
                                                minute: Math.max(value.minute, props.minDate.minute),
                                                second: Math.max(value.second, props.minDate.second),
                                            });
                                        }

                                        if (props.maxDate && value.hasSame(props.maxDate, 'day')) {
                                            adjustedValue = value.set({
                                                hour: Math.min(value.hour, props.maxDate.hour),
                                                minute: Math.min(value.minute, props.maxDate.minute),
                                                second: Math.min(value.second, props.maxDate.second),
                                            });
                                        }

                                        setShownDate(adjustedValue);
                                    }
                                    : () => { }
                            }
                        >
                            {text}
                        </div>
                    ))}
                </div>
            )))

    }, [rows, shownDate, props.minDate, props.maxDate, currentDate]);

    return (
        <div className="datetime-picker-panel-wrapper">
            <div className="datetime-picker-panel-wrapper-ref">
                <div
                    ref={ref}
                    style={props.calendarPosition}
                    className="datetime-picker-select-panel"
                >
                    {renderDatePickerHeader}

                    <div className={"datetime-picker-calendar-header"}>
                        <div className={"datetime-picker-calendar-cell"}>Su</div>
                        <div className={"datetime-picker-calendar-cell"}>Mo</div>
                        <div className={"datetime-picker-calendar-cell"}>Tu</div>
                        <div className={"datetime-picker-calendar-cell"}>We</div>
                        <div className={"datetime-picker-calendar-cell"}>Th</div>
                        <div className={"datetime-picker-calendar-cell"}>Fr</div>
                        <div className={"datetime-picker-calendar-cell"}>Sa</div>
                    </div>

                    {renderDatePickerCells}

                    <TimePicker shownDate={shownDate} setShownDate={setShownDate} minDate={props.minDate} maxDate={props.maxDate}/>

                    <div className="options">
                        <div className="options-clear" onClick={handleSelectDate(undefined)}>
                            Clear selection
                        </div>
                        <div className="options-confirm" onClick={handleSelectDate(shownDate)}>
                            Select
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}




